import { useEffect, useRef } from 'react'

/**
 * Re-run `refresh` while the page is actually in front of somebody.
 *
 * Website review comments are written on the client's own site by the
 * `website-review` edge function, so nothing the portal does produces them and
 * the queue has to find out on its own. Both review pages claimed to do that
 * over Supabase realtime — and the `supabase_realtime` publication in this
 * database carried no tables at all, so a `postgres_changes` channel subscribed
 * happily and was never sent a single event. The admin queue was a snapshot
 * taken when the review was opened, presented as live.
 *
 * Migration 0043 finished the job: `messages`, `image_annotations` and
 * `invoices` are published too now, so every channel in the app fires, and
 * `src/__tests__/realtimeSubscriptions.test.js` fails the build if a
 * subscription is ever added for a table no migration publishes.
 *
 * This is still worth having. Realtime is the fast path, not the only one — a
 * websocket drops, a deploy races a migration — and a chat that has silently
 * stopped updating looks exactly like a quiet one. A refetch when the tab comes
 * back to the foreground covers the case that actually happens —
 * the studio switches to the client's site, talks it through, and switches
 * back — and the interval only ticks while the document is visible, so a
 * forgotten tab costs nothing.
 */
export function useVisibleRefresh(refresh, { intervalMs = 30000, enabled = true } = {}) {
  const latest = useRef(refresh)
  latest.current = refresh

  useEffect(() => {
    if (!enabled) return undefined

    const run = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      latest.current?.()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') run()
    }

    const timer = intervalMs > 0 ? setInterval(run, intervalMs) : null
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', run)

    return () => {
      if (timer) clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', run)
    }
  }, [enabled, intervalMs])
}
